import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { withNamespaces } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { handleApiErrors } from '../../api/axiosInstance';
import RateQuotesAPI from '../../api/rate-quotes';
import { ConfirmModal } from '../../components';
import RateQuotesTable from '../../components/Table/RateQuotesTable';
import Toast from '../../components/Toast';
import PageContainer from '../../containers/PageContainer';
import withAuthentication from '../../hocs/withAuthentication';
import usePaginatedFiltersQuery from '../../hooks/queries/usePaginatedFiltersQuery';
import useModal from '../../hooks/useModal';
import useTableSort from '../../hooks/useTableSort';
import { formatPageTitle } from '../../utils/common';

const RateQuotesView = props => {
  const { t } = props;
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const [
    isDeleteConfirmationModalVisible,
    openDeleteConfirmationModal,
    closeDeleteConfirmationModal,
    deleteConfirmationModalProps,
  ] = useModal();

  const { stringTableSort, handleTableSort } = useTableSort({ created: -1 });

  const {
    query,
    paginationConfig,
    handlePageChange,
    handlePageSizeChange,
  } = usePaginatedFiltersQuery({
    staleTime: 0,
    queryKey: ['fetchRateQuotes', searchTerm, stringTableSort],
    queryFn: () =>
      RateQuotesAPI.fetchRateQuotes(
        { searchTerm, sort: stringTableSort },
        paginationConfig.current,
        paginationConfig.pageSize,
      ),
  });

  const deleteMutation = useMutation(id => RateQuotesAPI.deleteRateQuote(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['fetchRateQuotes']);
      Toast({
        type: 'success',
        message: t('rateQuoteDeletedSuccess'),
      });
      closeDeleteConfirmationModal();
    },
    onError: error => {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('rateQuoteDeleteError'),
        });
      });
    },
  });

  const handleDeleteClick = id => {
    openDeleteConfirmationModal({
      message: t('areYouSureYouWantToDeleteThisRateQuote?'),
      okText: t('yes'),
      cancelText: t('no'),
      onOk: () => deleteMutation.mutateAsync(id),
    });
  };

  return (
    <PageContainer title={t('rateQuotes')}>
      <Helmet>
        <title>{formatPageTitle(t('rateQuotes'))}</title>
      </Helmet>

      <RateQuotesTable
        t={t}
        loading={query.isFetching || deleteMutation.isLoading}
        dataSource={query.data?.documents || []}
        onDeleteRateClick={handleDeleteClick}
        onSearchTermChange={setSearchTerm}
        defaultSearchTerm={searchTerm}
        onChange={({ current }, filters, sorters) => {
          handlePageChange(current);
          handleTableSort(sorters?.columnKey, sorters?.order);
        }}
        pagination={{
          pageSize: paginationConfig.pageSize,
          total: paginationConfig.total,
          current: paginationConfig.current,
          onShowSizeChange: handlePageSizeChange,
        }}
      />

      <ConfirmModal
        visible={isDeleteConfirmationModalVisible}
        onCancel={closeDeleteConfirmationModal}
        loading={deleteMutation.isLoading}
        closable={!deleteMutation.isLoading}
        {...deleteConfirmationModalProps}
      />
    </PageContainer>
  );
};

export default withNamespaces()(withAuthentication(RateQuotesView));
